/**
 * Стандартні редактори документів ОЗ (рішення власника 2026-08-29 — «як
 * усі документи, бо кожен робить Дт-Кт»): введення в експлуатацію,
 * списання, продаж/передача. Канон: /new + /:id, DocumentToolbar (Закон
 * дизайну #6), .tch, DatePopover, чернетки форми, вкладення, дт-кт.
 *
 * Імпортовані з 1С (external_source='1c') — проведення ЗАБЛОКОВАНО:
 * їх проводки вже зайшли довідками (закриття/catch-all), статус картки —
 * з журналу подій; повторне проведення задвоїло б Дт-Кт.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assetCommissioning, assetWriteOff, fixedAssets, intangibleAssets, lowValueAssets,
  type AssetKind, type AssetStatus, type FixedAsset, type LowValueAsset,
  type TargetStatus, type WriteOffReason,
} from '@/api/assets';
import { sale as saleApi, type SaleItem } from '@/api/asset_inventory_sale';
import { contractors as contractorsApi } from '@/api/catalogs';
import { ASSET_KIND_UA } from '@/api/assetsRegistry';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import AssetPickerModal from './AssetPickerModal';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const errDetail = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
};
const today = () => new Date().toISOString().slice(0, 10);
// ч.915 (новий дизайн, Закон #10): блокування форми — fieldset у КОЖНОМУ блоці
// (канон ВМД): шапка блоку з діями лишається поза fieldset.
const FIELDSET_STYLE = { border: 'none', margin: 0, padding: 0, minWidth: 0 } as const;
type RailAction = { label: string; title?: string; disabled?: boolean; onClick: () => void };

type AnyAsset = (FixedAsset | LowValueAsset) & { accumulated_depreciation?: string; residual_value?: string; useful_life_months?: number };
type Cand = { kind: AssetKind; a: AnyAsset };

/** Кандидати-активи організації за статусами (усі 3 види або лише ОЗ). */
function useAssetCandidates(orgId: string, wanted: AssetStatus[], onlyFa = false) {
  const faQ = useQuery({ queryKey: ['fixed-assets-v2', orgId],
    queryFn: () => fixedAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId });
  const iaQ = useQuery({ queryKey: ['intangible-assets-v2', orgId],
    queryFn: () => intangibleAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId && !onlyFa });
  const lvQ = useQuery({ queryKey: ['low-value-assets', orgId],
    queryFn: () => lowValueAssets.list({ organization_id: orgId, limit: 500 }), enabled: !!orgId && !onlyFa });
  const all = useMemo<Cand[]>(() => [
    ...(faQ.data?.items ?? []).map((a) => ({ kind: 'FIXED_ASSET' as AssetKind, a: a as AnyAsset })),
    ...(iaQ.data?.items ?? []).map((a) => ({ kind: 'INTANGIBLE' as AssetKind, a: a as AnyAsset })),
    ...(lvQ.data?.items ?? []).map((a) => ({ kind: 'LOW_VALUE' as AssetKind, a: a as AnyAsset })),
  ], [faQ.data, iaQ.data, lvQ.data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.a.id, c])), [all]);
  const candidates = all.filter(({ a }) => wanted.includes(a.status));
  return { all, byId, candidates, loaded: !!faQ.data };
}

/** Кнопка «додати активи» → модалка мультивибору з фільтрами
 * (вид / підрозділ / МВО, рішення власника 2026-08-31). */
function AssetPicker({ orgId, candidates, used, onPick, disabled, kinds }: {
  orgId: string; candidates: Cand[]; used: Set<string>; onPick: (c: Cand) => void;
  disabled?: boolean; kinds?: AssetKind[];
}) {
  const [open, setOpen] = useState(false);
  if (disabled) return null;
  // ч.915: кнопка живе у шапці блоку ТЧ (DocBlock actions) — без обгортки.
  return (
    <>
      <button type="button" className="primary" onClick={() => setOpen(true)}
        disabled={!orgId} title={!orgId ? 'Спершу оберіть організацію' : 'Підбір активів (вид / підрозділ / МВО)'}>
        + додати активи
      </button>
      {open && (
        <AssetPickerModal orgId={orgId} candidates={candidates} used={used} kinds={kinds}
          onAdd={(list) => list.forEach(onPick)} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function ImportedBanner({ source }: { source?: string | null }) {
  if (source !== '1c') return null;
  return (
    <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)', borderRadius: 6,
      padding: '0.4rem 0.75rem', margin: '0.5rem 0', fontSize: '0.85rem' }}>
      <ImportedBadge source="1c" /> Документ імпортовано з 1С: його проводки вже зайшли довідкою, стан
      активу — з журналу подій. Проведення тут вимкнено, щоб не задвоїти Дт-Кт.
    </div>
  );
}

function OrgDateHeader({ orgId, setOrgId, orgs, date, setDate, disabled, children }: {
  orgId: string; setOrgId: (v: string) => void; orgs: { id: string; name: string }[];
  date: string; setDate: (v: string) => void; disabled: boolean; children?: React.ReactNode;
}) {
  return (
    <div className="doc-grid">
      <div className="field" style={{ gridColumn: 'span 5' }}><label>Організація *</label>
        <select value={orgId} disabled={disabled} onChange={(e) => setOrgId(e.target.value)}>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select></div>
      <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата *</label><DatePopover value={date} onChange={setDate} disabled={disabled} /></div>
      {children}
    </div>
  );
}

// =====================================================================
// Введення в експлуатацію
// =====================================================================
type CommRow = { asset_kind: AssetKind; asset_id: string; primary_cost_snapshot: string; useful_life_months_snapshot: number | null };

export function AssetCommissioningEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.asset_commissioning.create');
  const docQ = useQuery({ queryKey: ['asset-commissioning', id], queryFn: () => assetCommissioning.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(),
    target_status: 'OPERATING' as TargetStatus, comment: '', rows: [] as CommRow[],
  });
  useEffect(() => {
    if (!d) return;
    setForm({
      organization_id: d.organization_id, document_date: d.document_date.slice(0, 10),
      target_status: (d.target_status ?? 'OPERATING') as TargetStatus, comment: d.comment ?? '',
      rows: d.items.map((it) => ({
        asset_kind: it.asset_kind, asset_id: it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '',
        primary_cost_snapshot: it.primary_cost_snapshot, useful_life_months_snapshot: it.useful_life_months_snapshot ?? null,
      })),
    });
  }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted;
  const imported = d?.external_source === '1c';
  const locked = isPosted || imported;
  const { byId, candidates } = useAssetCandidates(orgEff, ['PARKED']);
  const used = new Set(form.rows.map((r) => r.asset_id));
  const [error, setError] = useState<string | null>(null);
  // Prefill з картки: /new?asset_id=
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const aid = sp.get('asset_id');
    if (!isNew || !aid || prefilled) return;
    const c = candidates.find((x) => x.a.id === aid);
    if (c) { addRow(c); setPrefilled(true); }
  });
  function addRow(c: Cand) {
    setForm((f) => ({ ...f, rows: [...f.rows, {
      asset_kind: c.kind, asset_id: c.a.id, primary_cost_snapshot: c.a.primary_cost,
      useful_life_months_snapshot: c.a.useful_life_months ?? null,
    }] }));
  }
  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`,
    target_status: form.target_status, comment: form.comment || null,
    items: form.rows.map((r, i) => ({
      line_number: i + 1, asset_kind: r.asset_kind,
      fixed_asset_id: r.asset_kind === 'FIXED_ASSET' ? r.asset_id : null,
      intangible_asset_id: r.asset_kind === 'INTANGIBLE' ? r.asset_id : null,
      low_value_asset_id: r.asset_kind === 'LOW_VALUE' ? r.asset_id : null,
      primary_cost_snapshot: r.primary_cost_snapshot, useful_life_months_snapshot: r.useful_life_months_snapshot,
    })),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['asset-commissioning'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      const doc = isNew ? await assetCommissioning.create(payload() as never)
        : await assetCommissioning.update(id!, payload() as never);
      if (andPost) {
        try { await assetCommissioning.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/commissioning/${doc.id}`, { replace: true });
          throw e;
        }
      }
      return doc;
    },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/commissioning/${doc.id}`, { replace: true }); },
    onError: (e) => setError(errDetail(e)),
  });
  // 2026-08-31: амортизація вже нараховувалась → 409 DEPRECIATION_EXISTS → confirm → force.
  const unpost = useMutation({
    mutationFn: (force?: boolean) => assetCommissioning.unpost(id!, !!force),
    onSuccess: invalidate,
    onError: (e) => {
      const detail = errDetail(e);
      if (detail.includes('DEPRECIATION_EXISTS')) {
        confirm(detail.replace('DEPRECIATION_EXISTS: ', '') + '\n\nРозпровести все одно?')
          .then((ok) => { if (ok) unpost.mutate(true); });
        return;
      }
      setError(detail);
    },
  });
  const remove = useMutation({ mutationFn: () => assetCommissioning.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/commissioning'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && form.rows.length > 0;
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Активи', required: 1, filled: form.rows.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? 'Нове введення в експлуатацію' : `Введення в експлуатацію ${d?.document_number ?? ''}`}
          {imported && <ImportedBadge source="1c" />}
        </h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/assets/commissioning')}
          onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew}
          onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          onUnpost={() => unpost.mutate(false)} unpostPending={unpost.isPending}
          deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/commissioning/${id}/print`)}
          entriesDoc={{ type: 'doc.asset_commissioning', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.asset_commissioning', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <OrgDateHeader orgId={orgEff} setOrgId={(v) => setForm({ ...form, organization_id: v, rows: [] })}
          orgs={availableOrgs} date={form.document_date} setDate={(v) => setForm({ ...form, document_date: v })} disabled={locked || !isNew}>
          <div className="field"><label>Цільовий стан</label>
            <select value={form.target_status} onChange={(e) => setForm({ ...form, target_status: e.target.value as TargetStatus })}>
              <option value="OPERATING">в експлуатації</option>
              <option value="PARKED">на зберіганні</option>
            </select></div>
        </OrgDateHeader>
      </fieldset>
      </DocBlock>

      <DocBlock index={2} title="Активи"
        actions={!locked ? (
          <AssetPicker orgId={orgEff} candidates={candidates} used={used} onPick={addRow} />
        ) : undefined}>
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <TchTable>
          <thead><tr><th>№</th><th>Вид</th><th>Актив</th><th style={{ textAlign: 'right' }}>Первісна вартість</th><th>Строк, міс</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>Додайте активи зі статусом «на зберіганні».</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.asset_id);
              return (
                <tr key={r.asset_id}>
                  <td>{i + 1}</td>
                  <td>{ASSET_KIND_UA[r.asset_kind]}</td>
                  <td>{c ? `${c.a.inventory_number} ${c.a.name}` : r.asset_id}</td>
                  <td style={{ textAlign: 'right' }}>
                    <input type="number" step="0.01" value={r.primary_cost_snapshot} style={{ textAlign: 'right', width: 130 }}
                      onChange={(e) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, primary_cost_snapshot: e.target.value } : x) })} />
                  </td>
                  <td>
                    <input type="number" min="0" value={r.useful_life_months_snapshot ?? ''} style={{ width: 80 }}
                      onChange={(e) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, useful_life_months_snapshot: e.target.value === '' ? null : Number(e.target.value) } : x) })} />
                  </td>
                  <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={FIELDSET_STYLE}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label>
            <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.asset_commissioning"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}

// =====================================================================
// Списання
// =====================================================================
type WoRow = { asset_kind: AssetKind; asset_id: string; primary_cost_snapshot: string; accumulated_depreciation_snapshot: string };
const REASONS: { value: WriteOffReason; label: string }[] = [
  { value: 'OBSOLETE', label: 'зношення / моральне старіння' }, { value: 'BROKEN', label: 'поломка' },
  { value: 'LOST', label: 'втрата / нестача' }, { value: 'OTHER', label: 'інше' },
];

export function AssetWriteOffEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.asset_write_off.create');
  const docQ = useQuery({ queryKey: ['asset-write-off', id], queryFn: () => assetWriteOff.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(),
    reason: 'OBSOLETE' as WriteOffReason, reason_details: '', comment: '', rows: [] as WoRow[],
  });
  useEffect(() => {
    if (!d) return;
    setForm({
      organization_id: d.organization_id, document_date: d.document_date.slice(0, 10),
      reason: d.reason, reason_details: d.reason_details ?? '', comment: d.comment ?? '',
      rows: d.items.map((it) => ({
        asset_kind: it.asset_kind, asset_id: it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '',
        primary_cost_snapshot: it.primary_cost_snapshot, accumulated_depreciation_snapshot: it.accumulated_depreciation_snapshot ?? '0',
      })),
    });
  }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted;
  const imported = d?.external_source === '1c';
  const locked = isPosted || imported;
  const { byId, candidates } = useAssetCandidates(orgEff, ['OPERATING', 'PARKED', 'CONSERVATION', 'REPAIR_LONG']);
  const used = new Set(form.rows.map((r) => r.asset_id));
  const [error, setError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const aid = sp.get('asset_id');
    if (!isNew || !aid || prefilled) return;
    const c = candidates.find((x) => x.a.id === aid);
    if (c) { addRow(c); setPrefilled(true); }
  });
  function addRow(c: Cand) {
    setForm((f) => ({ ...f, rows: [...f.rows, {
      asset_kind: c.kind, asset_id: c.a.id, primary_cost_snapshot: c.a.primary_cost,
      accumulated_depreciation_snapshot: c.a.accumulated_depreciation ?? (c.a as LowValueAsset).cost_written_off ?? '0',
    }] }));
  }
  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`,
    reason: form.reason, reason_details: form.reason_details || null, comment: form.comment || null,
    items: form.rows.map((r, i) => ({
      line_number: i + 1, asset_kind: r.asset_kind,
      fixed_asset_id: r.asset_kind === 'FIXED_ASSET' ? r.asset_id : null,
      intangible_asset_id: r.asset_kind === 'INTANGIBLE' ? r.asset_id : null,
      low_value_asset_id: r.asset_kind === 'LOW_VALUE' ? r.asset_id : null,
      primary_cost_snapshot: r.primary_cost_snapshot, accumulated_depreciation_snapshot: r.accumulated_depreciation_snapshot,
    })),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['asset-write-off'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      const doc = isNew ? await assetWriteOff.create(payload() as never) : await assetWriteOff.update(id!, payload() as never);
      if (andPost) {
        try { await assetWriteOff.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/write-off/${doc.id}`, { replace: true });
          throw e;
        }
      }
      return doc;
    },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/write-off/${doc.id}`, { replace: true }); },
    onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => assetWriteOff.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => assetWriteOff.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/write-off'); }, onError: (e) => setError(errDetail(e)) });
  const canSave = !!orgEff && form.rows.length > 0 && (form.reason !== 'OTHER' || !!form.reason_details.trim());
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  const detailsRequired = form.reason === 'OTHER';
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: detailsRequired ? 3 : 2,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0)
        + (detailsRequired && form.reason_details.trim() ? 1 : 0) },
    { id: 'items', title: 'Активи', required: 1, filled: form.rows.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? 'Нове списання' : `Списання ${d?.document_number ?? ''}`}
          {imported && <ImportedBadge source="1c" />}
        </h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/assets/write-off')}
          onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew}
          onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending}
          deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/write-off/${id}/print`)}
          entriesDoc={{ type: 'doc.asset_write_off', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.asset_write_off', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <OrgDateHeader orgId={orgEff} setOrgId={(v) => setForm({ ...form, organization_id: v, rows: [] })}
          orgs={availableOrgs} date={form.document_date} setDate={(v) => setForm({ ...form, document_date: v })} disabled={locked || !isNew}>
          <div className="field"><label>Причина</label>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value as WriteOffReason })}>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select></div>
        </OrgDateHeader>
        <div className="field"><label>Деталі причини{form.reason === 'OTHER' && <span style={{ color: 'red' }}> *</span>}</label>
          <input value={form.reason_details} onChange={(e) => setForm({ ...form, reason_details: e.target.value })} /></div>
      </fieldset>
      </DocBlock>

      <DocBlock index={2} title="Активи"
        actions={!locked ? (
          <AssetPicker orgId={orgEff} candidates={candidates} used={used} onPick={addRow} />
        ) : undefined}>
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <TchTable>
          <thead><tr><th>№</th><th>Вид</th><th>Актив</th><th style={{ textAlign: 'right' }}>Первісна</th>
            <th style={{ textAlign: 'right' }}>Знос</th><th style={{ textAlign: 'right' }}>Залишкова</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--muted)' }}>Додайте активи для списання.</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.asset_id);
              return (
                <tr key={r.asset_id}>
                  <td>{i + 1}</td>
                  <td>{ASSET_KIND_UA[r.asset_kind]}</td>
                  <td>{c ? `${c.a.inventory_number} ${c.a.name}` : r.asset_id}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(r.primary_cost_snapshot)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(r.accumulated_depreciation_snapshot)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(Number(r.primary_cost_snapshot) - Number(r.accumulated_depreciation_snapshot))}</td>
                  <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={FIELDSET_STYLE}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label>
            <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.asset_write_off"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}

// =====================================================================
// Продаж / передача ОЗ
// =====================================================================
type SaleRow = { fixed_asset_id: string; sale_price_without_vat: string; vat_rate_percent: string; vat_amount_override: string | null; item_note: string | null };

export function FixedAssetSaleEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [sp] = useSearchParams();
  const { availableOrgs } = usePermissionsByOrg('documents.fixed_asset_sale.create');
  const docQ = useQuery({ queryKey: ['fa-sale', id], queryFn: () => saleApi.get(id!), enabled: !isNew });
  const d = docQ.data;
  const [form, setForm] = useState({
    organization_id: sp.get('organization_id') ?? '', document_date: today(),
    buyer_mode: 'contractor' as 'contractor' | 'external', counterparty_id: '', external_buyer_name: '',
    external_buyer_tax_id: '', agreement_text: '', comment: '', rows: [] as SaleRow[],
  });
  useEffect(() => {
    if (!d) return;
    setForm({
      organization_id: d.organization_id, document_date: d.document_date.slice(0, 10),
      buyer_mode: d.counterparty_id ? 'contractor' : 'external',
      counterparty_id: d.counterparty_id ?? '', external_buyer_name: d.external_buyer_name ?? '',
      external_buyer_tax_id: d.external_buyer_tax_id ?? '', agreement_text: d.agreement_text ?? '',
      comment: d.comment ?? '',
      rows: d.items.map((it: SaleItem) => ({
        fixed_asset_id: it.fixed_asset_id, sale_price_without_vat: it.sale_price_without_vat,
        vat_rate_percent: it.vat_rate_percent ?? '20', vat_amount_override: it.vat_amount_override ?? null,
        item_note: it.item_note ?? null,
      })),
    });
  }, [d]);
  useFormDraft(form, setForm, { ready: isNew || !!d });
  const orgEff = form.organization_id || d?.organization_id || availableOrgs[0]?.id || '';
  const isPosted = !!d?.posted;
  const imported = d?.external_source === '1c';
  const locked = isPosted || imported;
  const { byId, candidates } = useAssetCandidates(orgEff, ['OPERATING', 'PARKED', 'CONSERVATION'], true);
  const contractorsQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const used = new Set(form.rows.map((r) => r.fixed_asset_id));
  const [error, setError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const aid = sp.get('asset_id');
    if (!isNew || !aid || prefilled) return;
    const c = candidates.find((x) => x.a.id === aid);
    if (c) { addRow(c); setPrefilled(true); }
  });
  function addRow(c: Cand) {
    setForm((f) => ({ ...f, rows: [...f.rows, {
      fixed_asset_id: c.a.id, sale_price_without_vat: c.a.residual_value ?? c.a.primary_cost,
      vat_rate_percent: '20', vat_amount_override: null, item_note: null,
    }] }));
  }
  const vatOf = (r: SaleRow) => r.vat_amount_override != null && r.vat_amount_override !== ''
    ? Number(r.vat_amount_override) : Math.round(Number(r.sale_price_without_vat) * Number(r.vat_rate_percent)) / 100;
  const totalNoVat = form.rows.reduce((s, r) => s + Number(r.sale_price_without_vat || 0), 0);
  const totalVat = form.rows.reduce((s, r) => s + vatOf(r), 0);
  const buyerOk = form.buyer_mode === 'contractor' ? !!form.counterparty_id : !!form.external_buyer_name.trim();
  const canSave = !!orgEff && buyerOk && form.rows.length > 0 && form.rows.every((r) => Number(r.sale_price_without_vat) > 0);
  const payload = () => ({
    organization_id: orgEff, document_number: null, document_date: `${form.document_date}T12:00:00`,
    counterparty_id: form.buyer_mode === 'contractor' ? form.counterparty_id : null,
    external_buyer_name: form.buyer_mode === 'external' ? form.external_buyer_name.trim() : null,
    external_buyer_tax_id: form.buyer_mode === 'external' && form.external_buyer_tax_id.trim() ? form.external_buyer_tax_id.trim() : null,
    agreement_text: form.agreement_text || null, comment: form.comment || null,
    items: form.rows.map((r, i) => ({
      line_number: i + 1, fixed_asset_id: r.fixed_asset_id, sale_price_without_vat: r.sale_price_without_vat,
      vat_rate_percent: r.vat_rate_percent || '20', vat_amount_override: r.vat_amount_override || null, item_note: r.item_note,
    })),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['fa-sale'] }); qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] }); };
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      const doc = isNew ? await saleApi.create(payload() as never) : await saleApi.update(id!, payload() as never);
      if (andPost) {
        try { await saleApi.post(doc.id); } catch (e) {
          // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
          if (isNew) navigate(`/assets/sale/${doc.id}`, { replace: true });
          throw e;
        }
      }
      return doc;
    },
    onSuccess: (doc) => { invalidate(); if (isNew) navigate(`/assets/sale/${doc.id}`, { replace: true }); },
    onError: (e) => setError(errDetail(e)),
  });
  const unpost = useMutation({ mutationFn: () => saleApi.unpost(id!), onSuccess: invalidate, onError: (e) => setError(errDetail(e)) });
  const remove = useMutation({ mutationFn: () => saleApi.remove(id!), onSuccess: () => { invalidate(); navigate('/assets/sale'); }, onError: (e) => setError(errDetail(e)) });
  // ч.915 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: RailAction[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (orgEff ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'buyer', title: 'Покупець', required: 1, filled: buyerOk ? 1 : 0 },
    { id: 'items', title: 'Основні засоби', required: 1, filled: form.rows.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? 'Новий продаж / передача ОЗ' : `Продаж / передача ОЗ ${d?.document_number ?? ''}`}
          {imported && <ImportedBadge source="1c" />}
        </h2>
        {/* 2026-08-29 (рішення власника): кнопки — праворуч у рядку під назвою (канон редактора реалізації). */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/assets/sale')}
          onSave={() => save.mutate(false)} saveDisabled={!canSave || locked} savePending={save.isPending}
          postable={!imported} isPosted={isPosted} isNew={isNew}
          onPost={() => save.mutate(true)} postDisabled={!canSave} postPending={save.isPending}
          onUnpost={() => unpost.mutate()} unpostPending={unpost.isPending}
          deletable={!isPosted}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/assets/sale/${id}/print`)}
          entriesDoc={{ type: 'doc.fixed_asset_sale', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>
      <ImportedBanner source={d?.external_source} />
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.fixed_asset_sale', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <OrgDateHeader orgId={orgEff} setOrgId={(v) => setForm({ ...form, organization_id: v, rows: [] })}
          orgs={availableOrgs} date={form.document_date} setDate={(v) => setForm({ ...form, document_date: v })} disabled={locked || !isNew}>
          <div className="field"><label>Тип покупця</label>
            <select value={form.buyer_mode} onChange={(e) => setForm({ ...form, buyer_mode: e.target.value as 'contractor' | 'external' })}>
              <option value="contractor">контрагент</option>
              <option value="external">фізособа / інший</option>
            </select></div>
        </OrgDateHeader>
      </fieldset>
      </DocBlock>

      <DocBlock index={2} title="Покупець">
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        {form.buyer_mode === 'contractor' ? (
          <div className="field"><label>Контрагент *</label>
            <select value={form.counterparty_id} onChange={(e) => setForm({ ...form, counterparty_id: e.target.value })}>
              <option value="">— оберіть —</option>
              {(contractorsQ.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
        ) : (
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 8' }}><label>Покупець (ПІБ / назва) *</label>
              <input value={form.external_buyer_name} onChange={(e) => setForm({ ...form, external_buyer_name: e.target.value })} /></div>
            <div className="field" style={{ gridColumn: 'span 4' }}><label>РНОКПП / ЄДРПОУ</label>
              <input value={form.external_buyer_tax_id} onChange={(e) => setForm({ ...form, external_buyer_tax_id: e.target.value })} /></div>
          </div>
        )}
        <div className="field"><label>Договір / підстава</label>
          <input value={form.agreement_text} onChange={(e) => setForm({ ...form, agreement_text: e.target.value })} /></div>
      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Основні засоби"
        actions={!locked ? (
          <AssetPicker orgId={orgEff} candidates={candidates} used={used} onPick={addRow} kinds={['FIXED_ASSET']} />
        ) : undefined}>
      <fieldset disabled={locked} style={FIELDSET_STYLE}>
        <TchTable>
          <thead><tr><th>№</th><th>Актив</th><th style={{ textAlign: 'right' }}>Залишкова</th>
            <th style={{ textAlign: 'right' }}>Ціна без ПДВ</th><th>ПДВ, %</th><th style={{ textAlign: 'right' }}>ПДВ</th>
            <th style={{ textAlign: 'right' }}>З ПДВ</th><th /></tr></thead>
          <tbody>
            {form.rows.length === 0 && <tr><td colSpan={8} style={{ color: 'var(--muted)' }}>Додайте ОЗ для продажу.</td></tr>}
            {form.rows.map((r, i) => {
              const c = byId.get(r.fixed_asset_id);
              const upd = (patch: Partial<SaleRow>) => setForm({ ...form, rows: form.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
              return (
                <tr key={r.fixed_asset_id}>
                  <td>{i + 1}</td>
                  <td>{c ? `${c.a.inventory_number} ${c.a.name}` : r.fixed_asset_id}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(c?.a.residual_value)}</td>
                  <td style={{ textAlign: 'right' }}><input type="number" step="0.01" value={r.sale_price_without_vat}
                    style={{ textAlign: 'right', width: 120 }} onChange={(e) => upd({ sale_price_without_vat: e.target.value })} /></td>
                  <td><select value={r.vat_rate_percent} onChange={(e) => upd({ vat_rate_percent: e.target.value, vat_amount_override: null })}>
                    {['20', '14', '7', '0'].map((v) => <option key={v} value={v}>{v}</option>)}</select></td>
                  <td style={{ textAlign: 'right' }}><input type="number" step="0.01" value={r.vat_amount_override ?? vatOf(r).toFixed(2)}
                    style={{ textAlign: 'right', width: 100 }} onChange={(e) => upd({ vat_amount_override: e.target.value })} /></td>
                  <td style={{ textAlign: 'right' }}>{fmt2(Number(r.sale_price_without_vat || 0) + vatOf(r))}</td>
                  <td><button type="button" className="danger" onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
          {form.rows.length > 0 && <tfoot><tr>
            <td colSpan={3}>Разом</td><td style={{ textAlign: 'right' }}>{fmt2(totalNoVat)}</td><td />
            <td style={{ textAlign: 'right' }}>{fmt2(totalVat)}</td><td style={{ textAlign: 'right' }}>{fmt2(totalNoVat + totalVat)}</td><td />
          </tr></tfoot>}
        </TchTable>
      </fieldset>
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <fieldset disabled={locked} style={FIELDSET_STYLE}>
          <div className="field" style={{ margin: 0 }}><label>Коментар</label>
            <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
        </fieldset>
        <div>
          <AttachmentsPanel organizationId={orgEff} documentType="doc.fixed_asset_sale"
            documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}
